/**
 * "Fit widths" as a command to the grid showing a screen, not a config edit.
 *
 * It used to clear every saved width and let the grid re-measure what had none
 * — a side effect behind a cache key, a row count and a screen's own layer, any
 * of which could leave a click doing nothing (owner, 2026-10-16: "some time the
 * button is not working"). Now the editor asks, the grid showing that panel
 * measures **now** and writes the widths it measured back as the screen's own,
 * so every click visibly does something and the result is kept.
 *
 * The editor and the grid sit in different subtrees of the host, and this
 * island may not reach into the host for a shared store — so the channel lives
 * here, keyed by the panel's `ScreenerConfig.id`.
 */

/** `data`: sized to the values. `names`: wide enough for the header too. */
export type FitMode = "data" | "names";

type Listener = (mode: FitMode) => void;

const listeners = new Map<string, Set<Listener>>();

/** Ask the grid showing panel `id` to fit. False when no grid is listening. */
export function requestFit(id: string | undefined, mode: FitMode): boolean {
  if (!id) return false;
  const set = listeners.get(id);
  if (!set || set.size === 0) return false;
  for (const listener of [...set]) listener(mode);
  return true;
}

/** The grid's half: hear fit requests for panel `id`. Returns the unsubscribe. */
export function onFitRequest(id: string, listener: Listener): () => void {
  let set = listeners.get(id);
  if (!set) listeners.set(id, (set = new Set()));
  set.add(listener);
  return () => {
    set!.delete(listener);
    if (set!.size === 0) listeners.delete(id);
  };
}
